import { Button } from "../components/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, } from "../components/Collapsible";
import { Counter } from "../components/Counter";
import { Icon } from "../components/Icon";
import { LinkWrapper, } from "../components/LinkWrapper";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { ChevronDownIcon, ChevronUpIcon, MoreIcon } from "../icons/app";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const NavigationListItemStyles = cva(cn("s-box-border s-flex s-items-center s-w-full s-gap-1.5 s-cursor-pointer s-select-none", "s-items-center s-outline-none s-rounded-xl s-text-sm s-px-3 s-py-2 s-transition-colors s-duration-300", "data-[disabled]:s-pointer-events-none", "data-[disabled]:s-text-muted-foreground dark:data-[disabled]:s-text-muted-foreground-night", "hover:s-text-foreground dark:hover:s-text-foreground-night", "hover:s-bg-primary-100 dark:hover:s-bg-primary-200-night"), {
    variants: {
        state: {
            active: "active:s-bg-primary-150 dark:active:s-bg-primary-200-night",
            selected: cn("s-text-foreground dark:s-text-foreground-night", "s-bg-primary-100 dark:s-bg-primary-200-night"),
            unselected: "s-text-muted-foreground dark:s-text-muted-foreground-night",
        },
    },
    defaultVariants: {
        state: "unselected",
    },
});
const NavigationList = React.forwardRef(({ className, children, viewportRef, ...props }, ref) => {
    return (React.createElement(ScrollArea, { ref: ref, viewportRef: viewportRef, className: cn(className, "s-transition-all s-duration-300"), ...props },
        React.createElement("div", { className: "s-flex s-flex-col s-gap-0.5" }, children),
        React.createElement(ScrollBar, null)));
});
NavigationList.displayName = "NavigationList";
const NavigationListItem = React.forwardRef(({ className, selected, label, icon, avatar, href, target, rel, replace, shallow, moreMenu, status = "idle", count, hasActivity, ...props }, ref) => {
    const [isPressed, setIsPressed] = React.useState(false);
    const handleMouseDown = (event) => {
        if (!event.target.closest(".button-class")) {
            setIsPressed(true);
        }
    };
    const getStatusDotColor = () => {
        switch (status) {
            case "unread":
                return "s-h-2 s-w-2 s-m-1 s-bg-highlight-500 dark:s-bg-highlight-500-night";
            case "blocked":
                return "s-h-2 s-w-2 s-m-1 s-bg-golden-400 dark:s-bg-golden-400-night";
            case "error":
                return "s-h-2 s-w-2 s-m-1 s-bg-warning-400 dark:s-bg-warning-400-night";
            default:
                return "";
        }
    };
    const shouldShowStatusDot = status !== "idle";
    const counterValue = count && count > 0 ? count : undefined;
    const shouldHideStatusIndicators = Boolean(moreMenu && selected);
    return (React.createElement("div", { className: cn("s-group/menu-item s-relative", className), ref: ref, "data-nav": "menu-button", "data-selected": selected, ...props },
        React.createElement(LinkWrapper, { href: href, target: target, rel: rel, replace: replace, shallow: shallow },
            React.createElement("div", { className: cn("s-peer/menu-button", NavigationListItemStyles({
                    state: selected
                        ? "selected"
                        : isPressed
                            ? "active"
                            : "unselected",
                })), onMouseLeave: () => {
                    setIsPressed(false);
                }, onMouseDown: handleMouseDown, onMouseUp: () => setIsPressed(false) },
                icon && React.createElement(Icon, { visual: icon, size: "xs", className: "s-m-0.5" }),
                avatar,
                label && (React.createElement("span", { className: cn("s-grow s-overflow-hidden s-text-ellipsis s-whitespace-nowrap group-focus-within/menu-item:s-pr-8 group-hover/menu-item:s-pr-8 group-data-[selected=true]/menu-item:s-pr-8", hasActivity && "s-font-semibold") }, label)),
                counterValue !== undefined && !shouldHideStatusIndicators && (React.createElement(Counter, { value: counterValue, size: "xs", variant: "outline", className: cn("s-flex-shrink-0 s-translate-x-0.5", moreMenu &&
                        "group-focus-within/menu-item:s-hidden group-hover/menu-item:s-hidden") })),
                shouldShowStatusDot && !shouldHideStatusIndicators && (React.createElement("div", { className: cn("s-heading-xs s-flex s-flex-shrink-0 s-items-center s-justify-center s-rounded-full", moreMenu &&
                        "group-focus-within/menu-item:s-hidden group-hover/menu-item:s-hidden", getStatusDotColor()) })))),
        moreMenu && React.createElement(React.Fragment, null, moreMenu)));
});
NavigationListItem.displayName = "NavigationListItem";
const NavigationListItemAction = React.forwardRef(({ className, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, "data-sidebar": "menu-action", className: cn("s-absolute s-right-2 s-top-1.5 s-opacity-0 s-transition-opacity", "s-opacity-0 group-focus-within/menu-item:s-opacity-100 group-hover/menu-item:s-opacity-100", className), ...props },
        React.createElement(Button, { size: "xmini", icon: MoreIcon, variant: "ghost" })));
});
NavigationListItemAction.displayName = "NavigationListItemAction";
const variantStyles = cva("", {
    variants: {
        variant: {
            primary: "s-text-foreground dark:s-text-foreground-night",
            secondary: "s-text-muted-foreground dark:s-text-muted-foreground-night",
        },
        isSticky: {
            true: cn("s-sticky s-top-0 s-z-10 s-bg-background dark:s-bg-muted-background-night", "s-border-border dark:s-border-border-night"),
        },
    },
    defaultVariants: {
        variant: "primary",
        isSticky: false,
    },
});
const labelStyles = cva("s-flex s-items-center s-justify-between s-gap-2 s-pt-4 s-pb-2 s-pr-2 s-heading-xs s-whitespace-nowrap s-overflow-hidden s-text-ellipsis");
const NavigationListLabel = React.forwardRef(({ className, variant, label, isSticky, action, ...props }, ref) => (React.createElement("div", { ref: ref, className: cn(labelStyles(), variantStyles({ variant, isSticky }), "s-pl-3", className), ...props },
    React.createElement("div", { className: "s-flex s-items-center s-gap-1 s-overflow-hidden s-text-ellipsis" },
        React.createElement("span", { className: "s-overflow-hidden s-text-ellipsis" }, label)),
    action)));
NavigationListLabel.displayName = "NavigationListLabel";
const variantCompactStyles = cva("s-flex s-px-2 s-py-1 s-pl-3 s-text-[10px] s-font-semibold s-text-foreground dark:s-text-foreground-night s-pt-3 s-uppercase s-whitespace-nowrap s-overflow-hidden s-text-ellipsis", {
    variants: {
        isSticky: {
            true: cn("s-sticky s-top-0 s-z-10 s-bg-muted-background dark:s-bg-muted-background-night", "s-border-border dark:s-border-border-night"),
        },
    },
    defaultVariants: {
        isSticky: false,
    },
});
const NavigationListCompactLabel = React.forwardRef(({ className, label, isSticky, ...props }, ref) => (React.createElement("div", { ref: ref, className: cn(variantCompactStyles({ isSticky }), className), ...props },
    React.createElement("div", { className: "s-flex s-items-center s-gap-1 s-overflow-hidden s-text-ellipsis" }, label))));
NavigationListCompactLabel.displayName = "NavigationListCompactLabel";
const collapseableStyles = cva(cn("s-py-2 s-px-2.5 s-w-full s-flex-1 s-text-left s-w-full", "s-heading-xs s-whitespace-nowrap s-overflow-hidden s-text-ellipsis", "s-select-none", "s-outline-none s-rounded-xl s-transition-colors s-duration-300", "data-[disabled]:s-pointer-events-none", "data-[disabled]:s-text-muted-foreground dark:data-[disabled]:s-text-muted-foreground-night"), {
    variants: {
        variant: {
            primary: "s-text-foreground dark:s-text-foreground-night",
            secondary: "s-text-muted-foreground dark:s-text-muted-foreground-night",
        },
        isCollapsible: {
            true: cn("s-cursor-pointer s-mb-0.5", "hover:s-text-foreground dark:hover:s-text-foreground-night", "hover:s-bg-primary-100 dark:hover:s-bg-primary-200-night"),
            false: "",
        },
    },
    defaultVariants: {
        variant: "primary",
        isCollapsible: false,
    },
});
const NavigationListCollapsibleSection = React.forwardRef(({ label, action, actionOnHover = true, children, className, type = "static", variant = "primary", defaultOpen, open, onOpenChange, visibleItems, ...props }, ref) => {
    const [isShowingAll, setIsShowingAll] = React.useState(false);
    const childArray = React.Children.toArray(children);
    const hasPartialCollapse = visibleItems !== undefined && visibleItems < childArray.length;
    const visibleChildrenSlice = hasPartialCollapse && !isShowingAll
        ? childArray.slice(0, visibleItems)
        : childArray;
    const overflowChildren = hasPartialCollapse && !isShowingAll ? childArray.slice(visibleItems) : [];
    const isCollapsible = type !== "static";
    const labelElement = (React.createElement("div", { className: collapseableStyles({ variant, isCollapsible }) }, label));
    const actionElement = action && (React.createElement("div", { className: cn("s-m-1.5 s-flex s-gap-1 s-pr-0.5 s-transition-opacity", actionOnHover
            ? "s-opacity-0 hover:s-opacity-100 group-focus-within/menu-item:s-opacity-100 group-hover/menu-item:s-opacity-100"
            : "s-opacity-100"), onClick: (e) => {
            e.stopPropagation();
        } }, action));
    const handleOpenChange = (newOpen) => {
        if (!newOpen) {
            setIsShowingAll(false);
        }
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(newOpen);
    };
    const renderedContent = (React.createElement("div", { className: "s-flex s-flex-col s-gap-0.5" },
        visibleChildrenSlice,
        hasPartialCollapse && (React.createElement(Collapsible, { open: isShowingAll, onOpenChange: setIsShowingAll },
            React.createElement(CollapsibleContent, null,
                React.createElement("div", { className: "s-flex s-flex-col s-gap-0.5" }, overflowChildren)),
            React.createElement("div", { className: "s-px-1.5 s-py-1 s-gap-1 s-flex" }, isShowingAll ? (React.createElement(Button, { size: "xs", icon: ChevronUpIcon, variant: "ghost-secondary", label: "Hide", onClick: () => setIsShowingAll(false) })) : (React.createElement(Button, { size: "xs", icon: ChevronDownIcon, variant: "ghost-secondary", label: "Show all", onClick: () => setIsShowingAll(true) })))))));
    if (type === "static") {
        return (React.createElement("div", { ref: ref, className: className, ...props },
            React.createElement("div", { className: "s-group/menu-item s-relative s-mt-2 s-flex s-flex-1 s-items-center s-justify-start s-gap-1" },
                labelElement,
                actionElement),
            renderedContent));
    }
    const collapsibleProps = {
        defaultOpen,
        open,
        onOpenChange: handleOpenChange,
        ...props,
    };
    if (type === "collapseAndScroll") {
        return (React.createElement(Collapsible, { ref: ref, className: className, ...collapsibleProps },
            React.createElement("div", { className: "s-group/menu-item s-relative s-mt-2 s-flex s-flex-1 s-items-center s-justify-start s-gap-1" },
                React.createElement(CollapsibleTrigger, { hideChevron: true }, labelElement),
                actionElement),
            React.createElement(CollapsibleContent, null,
                React.createElement(ScrollArea, null,
                    renderedContent,
                    React.createElement(ScrollBar, null)))));
    }
    // type === "collapse" (default collapsible behavior)
    return (React.createElement(Collapsible, { ref: ref, className: className, ...collapsibleProps },
        React.createElement("div", { className: "s-group/menu-item s-relative s-mt-2 s-flex s-flex-1 s-items-center s-justify-start s-gap-1" },
            React.createElement(CollapsibleTrigger, { hideChevron: true }, labelElement),
            actionElement),
        React.createElement(CollapsibleContent, null, renderedContent)));
});
NavigationListCollapsibleSection.displayName =
    "NavigationListCollapsibleSection";
export { NavigationList, NavigationListCollapsibleSection, NavigationListCompactLabel, NavigationListItem, NavigationListItemAction, NavigationListLabel, };
//# sourceMappingURL=NavigationList.js.map